import { useCallback } from 'react';
import { convertStringBooleanAndClean } from '../../../../utils/conversionUtils';
import { landingGearHasSubAssembly } from '../../../../utils/landingGearAttachUtils';

export function useComponentMetadata() {
  
  const extractMetadata = useCallback((component: any): Record<string, any> => {
    let fromCustomFields: Record<string, any> = {};

    if (component?.customFields) {
      try {
        const parsed = JSON.parse(component.customFields.trim());
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          fromCustomFields = parsed as Record<string, any>;
        }
      } catch {
        fromCustomFields = {};
      }
    }

    let fromDetails: Record<string, any> = {};
    if (component) {
      const componentType = component.componentType;

      const isEngine =
        componentType === 0 || componentType === "Engine";
      const isAPU = componentType === 1 || componentType === "APU";
      const isLandingGear =
        componentType === 2 ||
        componentType === "LandingGear" ||
        componentType === "Landing Gear";

      if (isEngine && component.engineDetails) {
        let eng = component.engineDetails;
        if (Array.isArray(eng)) eng = eng[0];
        if (eng) {
          fromDetails = {
            engineStatus: eng.engineStatus || undefined,
            engineDateLastShopVisit: eng.dateLastShopVisit || undefined,
            engineLlpStatus: eng.llpStatus || undefined,
            engineTsnLastShopVisit: eng.tsnLastShopVisit || undefined,
            engineCsnLastShopVisit: eng.csnLastShopVisit || undefined,
            engineAsOfDate: eng.asOfDate || undefined,
            shopVisitReasonId: eng.shopVisitReasonId || undefined,
            shopVisitReasonLabel: eng.shopVisitReasonLabel || undefined,
            engineCurrentThrust: eng.currentOperatingThrust || undefined,
          };
        }
      } else if (isAPU && component.apuDetails) {
        let apu = component.apuDetails;
        if (Array.isArray(apu)) apu = apu[0];
        if (apu) {
          fromDetails = {
            apuPartNumber: apu.partNumber || undefined,
            apuStatus: apu.apuStatus || undefined,
            apuTsn: apu.tsn || undefined,
            apuCsn: apu.csn || undefined,
            apuAsOfDate: apu.asOfDate || undefined,
            shopVisitReasonId: apu.shopVisitReasonId || undefined,
            shopVisitReasonLabel: apu.shopVisitReasonLabel || undefined,
            apuLastHsiOverhaulDate: apu.lastHSIOverhaulDate || undefined,
            apuTsnLastHsiOverhaul: apu.tsN_LastHSIOverhaul || undefined,
            apuCsnLastHsiOverhaul: apu.csN_LastHSIOverhaul || undefined,
            apuCycles: apu.cycles || undefined,
            apuLlpStatus: apu.llpStatus || undefined,
            apuCyclesRemainingToFirstLlp: apu.cyclesRemainingToFirstLLP || undefined,
            apuAnnualUtilization: apu.annualUtilization || undefined,
          };
        }
      } else if (isLandingGear && component.landingGearDetails) {
        let lg = component.landingGearDetails;
        if (Array.isArray(lg)) lg = lg[0];
        if (lg) {
          let lgCustomFields: Record<string, any> = {};
          if (lg.customFields) {
            try {
              const parsed = JSON.parse(String(lg.customFields).trim());
              if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                lgCustomFields = parsed as Record<string, any>;
              }
            } catch {
              lgCustomFields = {};
            }
          }

          fromDetails = {
            lgTsn: lg.tsn || undefined,
            lgCsn: lg.csn || undefined,
            lgAsOfDate: lg.asOfDate || undefined,
            shopVisitReasonId: lg.shopVisitReasonId || undefined,
            shopVisitReasonLabel: lg.shopVisitReasonLabel || undefined,
            lgLastOverhaulDate: lg.dateLastOverhaul || undefined,
            lgBackToBirth:
              lg.backToBirthTraceability === true
                ? "Yes"
                : lg.backToBirthTraceability === false
                  ? "No"
                  : undefined,
            lgTsnAtOverhaul: lg.tsnAtOverhaul || undefined,
            lgCsnAtOverhaul: lg.csnAtOverhaul || undefined,
            lgPosition: lg.position || undefined,
            lgHasSubAssembly: lgCustomFields.lgHasSubAssembly || undefined,
            lgAirframeFamily: lgCustomFields.lgAirframeFamily || undefined,
          };
        }
      }
    }

    const merged = { ...fromDetails, ...fromCustomFields };
    const offWing =
      merged.offWingType ??
      merged.OffWingType ??
      merged.OffWingStatus;
    if (offWing !== undefined && offWing !== null && merged.offWingType === undefined) {
      merged.offWingType = typeof offWing === "string" ? offWing : String(offWing);
    }

    return merged;
  }, []);

  
  const buildDetailObjects = useCallback(
    (type: string, customMetadata: any) => {
      let engineDetails = null;
      let apuDetails = null;
      let landingGearDetails = null;

      // customFields stores landing-gear flags not mapped to first-class columns
      const customFieldsJson = JSON.stringify(
        type === "LandingGear"
          ? {
              ...(customMetadata?.lgHasSubAssembly
                ? { lgHasSubAssembly: customMetadata.lgHasSubAssembly }
                : {}),
              ...(String(customMetadata?.lgAirframeFamily ?? "").trim()
                ? { lgAirframeFamily: String(customMetadata.lgAirframeFamily).trim() }
                : {}),
            }
          : {}
      );

      // Helper to clean values (remove empty strings and undefined/null)
      const cleanValue = (val: any) => {
        if (val === "" || val === undefined || val === null) return undefined;
        return val;
      };

      const hasLandingGearSubAssembly =
        type === "LandingGear" && landingGearHasSubAssembly(customMetadata);
      const isInShop =
        !hasLandingGearSubAssembly &&
        (customMetadata?.offWingType || "") === "In Shop";
      const reasonId = isInShop ? cleanValue(customMetadata?.shopVisitReasonId) : undefined;
      const reasonLabel = isInShop ? cleanValue(customMetadata?.shopVisitReasonLabel) : undefined;

      if (type === "Engine" && customMetadata) {
        engineDetails = {
          id: "",
          componentId: "",
          isDeleted: false,
          createdDate: new Date().toISOString(),
          dateLastShopVisit: cleanValue(customMetadata.engineDateLastShopVisit),
          llpStatus: cleanValue(customMetadata.engineLlpStatus),
          tsnLastShopVisit: cleanValue(customMetadata.engineTsnLastShopVisit),
          csnLastShopVisit: cleanValue(customMetadata.engineCsnLastShopVisit),
          asOfDate: cleanValue(customMetadata.engineAsOfDate),
          shopVisitReasonId: reasonId,
          shopVisitReasonLabel: reasonLabel,
          currentOperatingThrust: cleanValue(customMetadata.engineCurrentThrust),
          customFields: customFieldsJson,
        };
      }

      if (type === "APU" && customMetadata) {
        apuDetails = {
          id: "",
          componentId: "",
          isDeleted: false,
          createdDate: new Date().toISOString(),
          partNumber: cleanValue(customMetadata.apuPartNumber),
          tsn: cleanValue(customMetadata.apuTsn),
          csn: cleanValue(customMetadata.apuCsn),
          asOfDate: cleanValue(customMetadata.apuAsOfDate),
          shopVisitReasonId: reasonId,
          shopVisitReasonLabel: reasonLabel,
          lastHSIOverhaulDate: cleanValue(customMetadata.apuLastHsiOverhaulDate),
          tsN_LastHSIOverhaul: cleanValue(customMetadata.apuTsnLastHsiOverhaul),
          csN_LastHSIOverhaul: cleanValue(customMetadata.apuCsnLastHsiOverhaul),
          cycles: cleanValue(customMetadata.apuCycles),
          llpStatus: cleanValue(customMetadata.apuLlpStatus),
          cyclesRemainingToFirstLLP: cleanValue(customMetadata.apuCyclesRemainingToFirstLlp),
          annualUtilization: cleanValue(customMetadata.apuAnnualUtilization),
          customFields: customFieldsJson,
        };
      }

      if (type === "LandingGear" && customMetadata) {
        landingGearDetails = {
          id: "",
          componentId: "",
          isDeleted: false,
          createdDate: new Date().toISOString(),
          tsn: cleanValue(customMetadata.lgTsn),
          csn: cleanValue(customMetadata.lgCsn),
          asOfDate: cleanValue(customMetadata.lgAsOfDate),
          shopVisitReasonId: hasLandingGearSubAssembly ? undefined : reasonId,
          shopVisitReasonLabel: hasLandingGearSubAssembly ? undefined : reasonLabel,
          dateLastOverhaul: cleanValue(customMetadata.lgLastOverhaulDate),
          backToBirthTraceability: convertStringBooleanAndClean(customMetadata.lgBackToBirth),
          tsnAtOverhaul: cleanValue(customMetadata.lgTsnAtOverhaul),
          csnAtOverhaul: cleanValue(customMetadata.lgCsnAtOverhaul),
          customFields: customFieldsJson,
        };
      }

      return { engineDetails, apuDetails, landingGearDetails };
    },
    []
  );

  return { extractMetadata, buildDetailObjects };
}
